import type { FieldValues, Path, UseFormReturn } from "react-hook-form"
import { isApiError } from "@/api/client"

export interface ApplyErrorOptions<T extends FieldValues> {
  /** Map server field names to form paths when they differ (e.g. `{ new_password: "password" }`). */
  fieldMap?: Partial<Record<string, Path<T>>>
  /** Fallback message for non-validation errors (defaults to the error message). */
  fallbackMessage?: string
}

/** Whether a dotted form path (`settings.websocket_url`) exists in the form values. */
function hasPath(values: Record<string, unknown>, path: string): boolean {
  let cur: unknown = values
  for (const key of path.split(".")) {
    if (cur === null || typeof cur !== "object" || !Object.hasOwn(cur, key)) return false
    cur = (cur as Record<string, unknown>)[key]
  }
  return true
}

/**
 * Push an API error into a react-hook-form instance.
 *
 * - 422 `fields` that match a form field — nested ones by dotted path, e.g.
 *   `settings.websocket_url` — (or `fieldMap`) become inline field errors.
 * - Everything else (unknown fields, 409, 403, …) lands in `errors.root.server`,
 *   which {@link FormRootError} renders above the submit button.
 *
 * Returns true when at least one inline field error was set.
 */
export function applyApiErrorToForm<T extends FieldValues>(
  error: unknown,
  form: Pick<UseFormReturn<T>, "setError" | "getValues">,
  opts: ApplyErrorOptions<T> = {},
): boolean {
  const values = (form.getValues() ?? {}) as Record<string, unknown>
  let inline = false
  const leftovers: string[] = []

  if (isApiError(error) && error.fields) {
    for (const [field, message] of Object.entries(error.fields)) {
      const target = opts.fieldMap?.[field] ?? (hasPath(values, field) ? (field as Path<T>) : undefined)
      if (target) {
        form.setError(target, { type: "server", message }, { shouldFocus: !inline })
        inline = true
      } else {
        leftovers.push(`${field}: ${message}`)
      }
    }
    if (leftovers.length) form.setError("root.server", { type: "server", message: leftovers.join("\n") })
    return inline
  }

  const message =
    opts.fallbackMessage ?? (error instanceof Error ? error.message : typeof error === "string" ? error : "")
  if (message) form.setError("root.server", { type: "server", message })
  return false
}
